import { createAsset } from '@/endpoints/assets/createAsset';
import { deleteAsset } from '@/endpoints/assets/deleteAsset';
import { downloadAsset } from '@/endpoints/assets/downloadAsset';
import { createCustomField } from '@/endpoints/custom-fields/createCustomField';
import { createCustomFieldsBulk } from '@/endpoints/custom-fields/createCustomFieldsBulk';
import { deleteCustomField } from '@/endpoints/custom-fields/deleteCustomField';
import { getCustomFields } from '@/endpoints/custom-fields/getCustomFields';
import { getCustomFieldsCount } from '@/endpoints/custom-fields/getCustomFieldsCount';
import { createDashboardAlert } from '@/endpoints/dashboard/createDashboardAlert';
import { deleteDashboardAlert } from '@/endpoints/dashboard/deleteDashboardAlert';
import { getActiveOrderCounts } from '@/endpoints/dashboard/getActiveOrderCounts';
import { getAllDashboardAlerts } from '@/endpoints/dashboard/getAllDashboardAlerts';
import { getDashboardAlerts } from '@/endpoints/dashboard/getDashboardAlerts';
import { getDashboardMetrics } from '@/endpoints/dashboard/getDashboardMetrics';
import { getChannelItemStatusBySubscription } from '@/endpoints/dashboard/getChannelItemStatusBySubscription';
import { getLatestOrders } from '@/endpoints/dashboard/getLatestOrders';
import { getOrderCountByChannel } from '@/endpoints/dashboard/getOrderCountByChannel';
import { getOrganizationByName } from '@/endpoints/dashboard/getOrganizations';
import { getPartiallyRoutedOrdersCount } from '@/endpoints/dashboard/getPartiallyRoutedOrdersCount';
import { getSalesByChannel } from '@/endpoints/dashboard/getSalesByChannel';
import { updateDashboardAlert } from '@/endpoints/dashboard/updateDashboardAlert';
import { downloadErrorReport, downloadFile } from '@/endpoints/download';
import {
  createExport,
  createExportProfile,
  createRecurringExport,
  deleteExportProfileById,
  deleteRecurringExportById,
  getExportProfileById,
  getExportProfiles,
  getExportProfilesCount,
  getExports,
  getExportsCount,
  getExportType,
  getExportTypeFields,
  getRecurringExportByExportProfileId,
  getRecurringExportById,
  getRecurringExports,
  getRecurringExportsCount,
  previewExportTypeRecords,
  updateExportProfile,
  updateRecurringExport,
  validateRecurringExportSFTPDestination,
  validateRecurringExportTimeFormat,
} from '@/endpoints/exports';
import {
  createImport,
  createImportPreview,
  createImportProfile,
  createRecurringImport,
  deleteImportPreview,
  deleteImportProfile,
  deleteRecurringImport,
  finalizeImportPreview,
  getAllImports,
  getAllImportsCount,
  getImportPreview,
  getImportPreviews,
  getImportProfileById,
  getImportProfiles,
  getImportProfilesCount,
  getImportSourceHeaders,
  getImports,
  getImportsCount,
  getImportTypeFields,
  getPersistedImportProfiles,
  getRecurringImportById,
  getRecurringImports,
  killImport,
  reparseImportPreview,
  updateImportProfile,
  updateRecurringImport,
} from '@/endpoints/imports';
import {
  createKitComponent,
  createKitConfiguration,
  deleteKitComponent,
  deleteKitConfiguration,
  getKitConfigurations,
  updateKitComponent,
} from '@/endpoints/kit';
import {
  applyCategoryPlanRun,
  createCategoryPlanRun,
  decideCategoryPlanSuggestions,
  getCategoryPlanDestinations,
  getCategoryPlanRun,
  getCategoryPlanRuns,
  linkCategoryPlanRun,
} from '@/endpoints/channel/categoryPlans';
import {
  deleteGlobalCommonality,
  deleteSubscriptionCommonality,
  ensureCommonalityFields,
  getCommonalityChannels,
  getGlobalCommonality,
  getGlobalCommonalityAllowedValues,
  getGlobalCommonalityGrid,
  getSubscriptionCommonality,
  postGlobalCommonality,
  postGlobalCommonalityBulk,
  postSubscriptionCommonality,
} from '@/endpoints/channel/commonality';
import { ensureProductFields } from '@/endpoints/channel/ensureProductFields';
import {
  assignRun,
  cancelAssignment,
  cancelRun,
  createResolution,
  createRun,
  getAssignmentPlan,
  getAssignments,
  getDestinations,
  getRun,
  getRunAllowedValues,
  getRunFamilies,
  getRunFields,
  getRunGrid,
  getRunGroupings,
  getRunNextStepCsv,
  getRunNextSteps,
  getRunProducts,
  getRunRows,
  createGapsSheetExport,
  getGapsSheetExportStatus,
  getGapsSheetPlan,
  listResolutions,
  listRunHistory,
  prepareGapsSheet,
  previewResolution,
  listRecategorizations,
  recategorizeProducts,
  refreshFromCatalog,
  searchRunCategories,
  resumeRun,
  undoResolution,
} from '@/endpoints/channel/expansion';
import { getAllowedValues } from '@/endpoints/channel/getAllowedValues';
import { getAttributeSpec } from '@/endpoints/channel/getAttributeSpec';
import { getAttributionHistoryForProduct } from '@/endpoints/channel/getAttributionHistoryForProduct';
import { getLedgerOrderSummaries } from '@/endpoints/channel/getLedgerOrderSummaries';
import { getOrderLedgerEntries } from '@/endpoints/channel/getOrderLedgerEntries';
import { getOrderLedgerSummary } from '@/endpoints/channel/getOrderLedgerSummary';
import { getPackingSlipUrl } from '@/endpoints/channel/getPackingSlipUrl';
import { getPostingTemplate } from '@/endpoints/channel/getPostingTemplate';
import { getPostingTemplateHistory } from '@/endpoints/channel/getPostingTemplateHistory';
import { getRenderedValuesForProduct } from '@/endpoints/channel/getRenderedValuesForProduct';
import { getRenderedValuesGrid } from '@/endpoints/channel/getRenderedValuesGrid';
import {
  createGenericTemplate,
  deleteGenericTemplate,
  getGenericCategory,
  getGenericContainers,
  getGenericTemplate,
  getGenericTemplates,
  getGenericTemplateTypes,
  resolveSubscriptionIdForIntegration,
  saveGenericTemplate,
} from '@/endpoints/channel/genericTemplate';
import {
  getAvailableIntegrationActions,
  getIntegrationActionHistory,
  getIntegrationActionOverrides,
  invokeIntegrationAction,
} from '@/endpoints/channel/integrationActions';
import { listCategoryCertRequirements } from '@/endpoints/channel/listCategoryCertRequirements';
import { registerCertification } from '@/endpoints/channel/registerCertification';
import { createScheduleOverride, deleteScheduleOverride, updateScheduleOverride } from '@/endpoints/channel/scheduleOverride';
import { getServicePlatformCallsHealth } from '@/endpoints/channel/getServicePlatformCallsHealth';
import { searchSubscriptionNotifications } from '@/endpoints/channel/searchSubscriptionNotifications';
import { getTwigContextType } from '@/endpoints/channel/getTwigContextType';
import { acceptAiSuggestions, rejectAiSuggestions } from '@/endpoints/channel/aiSuggestions';
import { disconnectConsent } from '@/endpoints/channel/disconnectConsent';
import { getChannelErrorsWithSuggestionsOptimized } from '@/endpoints/channel/getChannelErrorsWithSuggestionsOptimized';
import { getPostingTemplateErrors } from '@/endpoints/channel/getPostingTemplateErrors';
import { makePostingChannelsReadRequest, makePostingChannelsWriteRequest } from '@/endpoints/channel/postingTemplateEditor';
import { deleteManualOverride, manualOverride } from '@/endpoints/channel/manualOverride';
import { listOverrides } from '@/endpoints/channel/listOverrides';
import { postTwigPreview } from '@/endpoints/channel/postTwigPreview';
import {
  createIntegration,
  createIntegrationSubscription,
  deleteIntegration,
  getFulfillmentProviders,
  getAdminIntegrationSubscriptions,
  getIntegration,
  getIntegrationAllowances,
  getIntegrationSubscriptions,
  getStoreIntegrationSubscriptions,
  getIntegrationTypes,
  getIntegrationWarehouses,
  getSalesChannels,
  startIntegrationSubscriptionConsent,
  updateIntegration,
} from '@/endpoints/integration';
import { bulkCreateIntegrationAllowances } from '@/endpoints/integration/bulkCreateIntegrationAllowances';
import { bulkUpdateIntegrationAllowances } from '@/endpoints/integration/bulkUpdateIntegrationAllowances';
import { getIntegrations } from '@/endpoints/integration/getIntegrations';
import { createInventoryDetail } from '@/endpoints/inventory/createInventoryDetail';
import { patchInventoryDetail } from '@/endpoints/inventory/patchInventoryDetail';
import { getOrderHistory } from '@/endpoints/order/getOrderHistory';
import { postOrder } from '@/endpoints/order/postOrder';
import { postOrderReturnBulk } from '@/endpoints/order/postOrderReturnBulk';
import { postShipAdviceFulfillment } from '@/endpoints/order/postShipAdviceFulfillment';
import { createApiKey } from '@/endpoints/organization/createApiKey';
import { createOrganization } from '@/endpoints/organization/createOrganization';
import { getOrganizationCreateStatus } from '@/endpoints/organization/getOrganizationCreateStatus';
import { clearQuickBooksTokens } from '@/endpoints/organization/clearQuickBooksTokens';
import {
  exportSuggestedFixes,
  createRecord,
  deleteRecord,
  getCatalogItemImages,
  getChannelItemMappings,
  getChannelSharedData,
  getInventoryRecords,
  getRecord,
  getPostingTemplateMetadata,
  getSharedData,
  getSuggestedFixCount,
  inviteOrganizationUser,
  rerouteShipAdviceItem,
  retryOrder,
  searchSuggestedFixes,
  updateDeliveryInformation,
  updateEditableRecord,
  updateRecord,
  updateOrganizationUser,
  uploadImage,
} from '@/endpoints/ordinaryOperations';
import { getDynamicChannelRows, getDynamicCount, getDynamicRows, getDynamicSearchCount, getDynamicSearchRows } from '@/endpoints/list/dynamicList';
import { createRole } from '@/endpoints/organization/createRole';
import { deleteApiKey } from '@/endpoints/organization/deleteApiKey';
import { deleteInvite } from '@/endpoints/organization/deleteInvite';
import { deleteRole } from '@/endpoints/organization/deleteRole';
import { deleteUser } from '@/endpoints/organization/deleteUser';
import { getApiKeys } from '@/endpoints/organization/getApiKeys';
import { getApiKeyDetails } from '@/endpoints/organization/getApiKeyDetails';
import { getInvites } from '@/endpoints/organization/getInvites';
import { getInviteByCode } from '@/endpoints/organization/getInviteByCode';
import { getCurrentOrganizationSettings } from '@/endpoints/organization/getCurrentOrganizationSettings';
import { getOrganizationFeatures } from '@/endpoints/organization/getOrganizationFeatures';
import { getOrganizationSettings } from '@/endpoints/organization/getOrganizationSettings';
import { getResources } from '@/endpoints/organization/getResources';
import { getRoleDetails } from '@/endpoints/organization/getRoleDetails';
import { getRoleOptions } from '@/endpoints/organization/getRoleOptions';
import { getRoles } from '@/endpoints/organization/getRoles';
import { getUsers } from '@/endpoints/organization/getUsers';
import { reactivateInvite } from '@/endpoints/organization/reactivateInvite';
import { regenerateApiKeySecret } from '@/endpoints/organization/regenerateApiKeySecret';
import { resendInvite } from '@/endpoints/organization/resendInvite';
import { resetUser2FA } from '@/endpoints/organization/resetUser2FA';
import { updateOrganizationSettings } from '@/endpoints/organization/updateOrganizationSettings';
import { updateOrganization } from '@/endpoints/organization/updateOrganization';
import { createOrganizationSetting } from '@/endpoints/organization/createOrganizationSetting';
import { updateOrganizationSetting } from '@/endpoints/organization/updateOrganizationSetting';
import { deleteOrganizationSetting } from '@/endpoints/organization/deleteOrganizationSetting';
import { updateRole } from '@/endpoints/organization/updateRole';
import { updateApiKey } from '@/endpoints/organization/updateApiKey';
import { acceptInvite } from '@/endpoints/organization/acceptInvite';
import { searchPreviewProducts } from '@/endpoints/preview/searchPreviewProducts';
import { validatePreviewEntity } from '@/endpoints/preview/validatePreviewEntity';
import { createCustomPrice } from '@/endpoints/pricing/createCustomPrice';
import { createPricingItem } from '@/endpoints/pricing/createPricingItem';
import { createPromotion } from '@/endpoints/pricing/createPromotion';
import { deleteCustomPrice } from '@/endpoints/pricing/deleteCustomPrice';
import { deletePromotion } from '@/endpoints/pricing/deletePromotion';
import { patchCustomPrice } from '@/endpoints/pricing/patchCustomPrice';
import { patchPromotion } from '@/endpoints/pricing/patchPromotion';
import { getProductById, getProducts, getProductsInBatches, getProductsWithDetails } from '@/endpoints/product/getProducts';
import { createCatalogItem } from '@/endpoints/product/createCatalogItem';
import { patchPricingItem } from '@/endpoints/product/patchPricingItem';
import { createReport } from '@/endpoints/reports/createReport';
import { getOrganizations } from '@/endpoints/user/getOrganizations';
import { getUser } from '@/endpoints/user/getUser';
import { createWarehouse, deleteWarehouse, getWarehouse, patchWarehouse } from '@/endpoints/warehouse';
import { buildImportPreviewCleanupRequest } from '@/endpoints/imports/buildImportPreviewCleanupRequest';
import {
  createAdminTargetOrganizationSetting,
  deleteAdminTargetOrganizationSetting,
  getAdminTargetIntegrationAllowances,
  getAdminTargetOrganization,
  getAdminTargetOrganizationSettings,
  resolveAdminTargetOrganization,
  updateAdminTargetOrganization,
  updateAdminTargetOrganizationSetting,
} from '@/endpoints/adminTarget';
import type { OrganizationId } from './identity';
import { GLOBAL_SCOPE, requestScopeFor, type RequestScope } from './scopedTransport';

// Explicit endpoint manifests.
//
// Only operations registered here are reachable through the bound surface -
// binding is by explicit registration, never by recursive structural
// inference over the endpoint barrel (which re-exports pure helpers and,
// until step 3, re-exported the raw clients). Domains join these manifests as
// they migrate: step 4 lands the read-only pilot, steps 5-7 add the rest.
// The contract matrix (docs/organization-identity/contract-matrix.md) is the
// source of truth for which manifest an operation belongs to; disagreement
// between the two is a review-blocking defect.

export const scopedEndpoints = {
  assets: { createAsset, deleteAsset, downloadAsset },
  customFields: { createCustomField, createCustomFieldsBulk, deleteCustomField, getCustomFields, getCustomFieldsCount },
  dashboard: {
    getActiveOrderCounts,
    getChannelItemStatusBySubscription,
    getDashboardAlerts,
    getDashboardMetrics,
    getLatestOrders,
    getOrderCountByChannel,
    getOrganizationByName,
    getPartiallyRoutedOrdersCount,
    getSalesByChannel,
  },
  downloads: { downloadErrorReport, downloadFile },
  exports: {
    createExport,
    createExportProfile,
    createRecurringExport,
    deleteExportProfileById,
    deleteRecurringExportById,
    getExportProfileById,
    getExportProfiles,
    getExportProfilesCount,
    getExports,
    getExportsCount,
    getExportType,
    getExportTypeFields,
    getRecurringExportByExportProfileId,
    getRecurringExportById,
    getRecurringExports,
    getRecurringExportsCount,
    previewExportTypeRecords,
    updateExportProfile,
    updateRecurringExport,
    validateRecurringExportSFTPDestination,
    validateRecurringExportTimeFormat,
  },
  imports: {
    createImport,
    createImportPreview,
    createImportProfile,
    createRecurringImport,
    deleteImportPreview,
    deleteImportProfile,
    deleteRecurringImport,
    finalizeImportPreview,
    getAllImports,
    getAllImportsCount,
    getImportPreview,
    getImportPreviews,
    getImportProfileById,
    getImportProfiles,
    getImportProfilesCount,
    getImportSourceHeaders,
    getImports,
    getImportsCount,
    getImportTypeFields,
    getPersistedImportProfiles,
    getRecurringImportById,
    getRecurringImports,
    killImport,
    reparseImportPreview,
    updateImportProfile,
    updateRecurringImport,
  },
  inventory: { createInventoryDetail, patchInventoryDetail },
  kit: {
    createKitComponent,
    createKitConfiguration,
    deleteKitComponent,
    deleteKitConfiguration,
    getKitConfigurations,
    updateKitComponent,
  },
  listData: { getDynamicChannelRows, getDynamicCount, getDynamicRows, getDynamicSearchCount, getDynamicSearchRows },
  commonality: { deleteSubscriptionCommonality, getSubscriptionCommonality, postSubscriptionCommonality },
  categoryPlans: {
    applyCategoryPlanRun,
    createCategoryPlanRun,
    decideCategoryPlanSuggestions,
    getCategoryPlanDestinations,
    getCategoryPlanRun,
    getCategoryPlanRuns,
    linkCategoryPlanRun,
  },
  globalCommonality: {
    deleteGlobalCommonality,
    ensureCommonalityFields,
    getCommonalityChannels,
    getGlobalCommonality,
    getGlobalCommonalityAllowedValues,
    getGlobalCommonalityGrid,
    postGlobalCommonality,
    postGlobalCommonalityBulk,
  },
  channelCatalog: {
    acceptAiSuggestions,
    deleteManualOverride,
    getAllowedValues,
    getAttributeSpec,
    getAttributionHistoryForProduct,
    getChannelErrorsWithSuggestionsOptimized,
    getPostingTemplate,
    getPostingTemplateErrors,
    getRenderedValuesForProduct,
    getRenderedValuesGrid,
    listOverrides,
    manualOverride,
    rejectAiSuggestions,
  },
  channelExpansion: {
    assignRun,
    cancelAssignment,
    cancelRun,
    createResolution,
    createRun,
    getAssignmentPlan,
    getAssignments,
    getDestinations,
    getRun,
    getRunAllowedValues,
    getRunFamilies,
    getRunFields,
    getRunGrid,
    getRunGroupings,
    getRunNextStepCsv,
    getRunNextSteps,
    getRunProducts,
    getRunRows,
    createGapsSheetExport,
    getGapsSheetExportStatus,
    getGapsSheetPlan,
    listResolutions,
    listRunHistory,
    prepareGapsSheet,
    previewResolution,
    listRecategorizations,
    recategorizeProducts,
    refreshFromCatalog,
    searchRunCategories,
    resumeRun,
    undoResolution,
  },
  genericTemplates: {
    getGenericTemplate,
    getGenericTemplates,
    getGenericTemplateTypes,
    createGenericTemplate,
    resolveSubscriptionIdForIntegration,
    getGenericContainers,
    getGenericCategory,
    saveGenericTemplate,
    deleteGenericTemplate,
  },
  integrationData: {
    getIntegrations,
    getIntegrationSubscriptions,
    getStoreIntegrationSubscriptions,
    getIntegrationAllowances,
    getSalesChannels,
    getFulfillmentProviders,
    getIntegrationWarehouses,
    createIntegrationSubscription,
    disconnectConsent,
    startIntegrationSubscriptionConsent,
  },
  integrationActions: {
    getAvailableIntegrationActions,
    getIntegrationActionOverrides,
    getIntegrationActionHistory,
    invokeIntegrationAction,
    createScheduleOverride,
    updateScheduleOverride,
    deleteScheduleOverride,
  },
  integrationChannel: { listCategoryCertRequirements, registerCertification },
  channelLedger: { getLedgerOrderSummaries, getOrderLedgerEntries, getOrderLedgerSummary },
  channelUtilities: { ensureProductFields, getPackingSlipUrl, getPostingTemplateHistory, getTwigContextType, postTwigPreview },
  order: { getOrderHistory, postOrder, postOrderReturnBulk, postShipAdviceFulfillment },
  ordinaryOperations: {
    createRecord,
    deleteRecord,
    exportSuggestedFixes,
    getCatalogItemImages,
    getChannelItemMappings,
    getChannelSharedData,
    getInventoryRecords,
    getRecord,
    getPostingTemplateMetadata,
    getSharedData,
    getSuggestedFixCount,
    inviteOrganizationUser,
    rerouteShipAdviceItem,
    retryOrder,
    searchSuggestedFixes,
    updateDeliveryInformation,
    updateEditableRecord,
    updateRecord,
    updateOrganizationUser,
    uploadImage,
  },
  organizationData: {
    clearQuickBooksTokens,
    getCurrentOrganizationSettings,
    getOrganizationFeatures,
    getOrganizationSettings,
    getResources,
    getRoleDetails,
    updateOrganizationSettings,
    updateRole,
  },
  preview: { searchPreviewProducts, validatePreviewEntity },
  pricing: { createCustomPrice, createPricingItem, createPromotion, deleteCustomPrice, deletePromotion, patchCustomPrice, patchPromotion },
  product: { createCatalogItem, getProductById, getProducts, getProductsInBatches, getProductsWithDetails, patchPricingItem },
  reports: { createReport },
  serviceHealth: { getServicePlatformCallsHealth },
  subscriptionNotifications: { searchSubscriptionNotifications },
  users: {
    createApiKey,
    createRole,
    deleteApiKey,
    deleteInvite,
    deleteRole,
    deleteUser,
    getApiKeys,
    getApiKeyDetails,
    getInvites,
    getRoleOptions,
    getRoles,
    getUsers,
    reactivateInvite,
    regenerateApiKeySecret,
    resendInvite,
    resetUser2FA,
    updateApiKey,
  },
  user: { getUser },
  warehouse: { createWarehouse, deleteWarehouse, getWarehouse, patchWarehouse },
} as const;

// Authenticated-global operations (tenant scope 'none', session auth). These
// are not bound per-organization; call them directly. Every member owns its
// GLOBAL_SCOPE internally and takes domain parameters first - the scope-first
// convention is reserved for scopedEndpoints, so unchecked JS callers can
// never hit a runtime missing-scope failure here.
export const globalEndpoints = {
  dashboardAlertsAdmin: { createDashboardAlert, deleteDashboardAlert, getAllDashboardAlerts, updateDashboardAlert },
  integrationAdmin: {
    bulkCreateIntegrationAllowances,
    bulkUpdateIntegrationAllowances,
    getIntegration,
    getIntegrations: (params: Parameters<typeof getIntegrations>[1], options: Parameters<typeof getIntegrations>[2]) =>
      getIntegrations(GLOBAL_SCOPE, params, options),
    createIntegration,
    updateIntegration,
    deleteIntegration,
    getIntegrationTypes,
    getIntegrationSubscriptions: getAdminIntegrationSubscriptions,
  },
  organization: { createOrganization, getOrganizationCreateStatus, resolveAdminTargetOrganization },
  user: { getOrganizations },
} as const;

// Cross-target operations are deliberately not exposed through useEndpoints.
// Their acting organization and administered target are different identities;
// callers must enter through the audited administrator-target surface.
export const adminTargetEndpoints = {
  organization: {
    createOrganizationSetting: createAdminTargetOrganizationSetting,
    deleteOrganizationSetting: deleteAdminTargetOrganizationSetting,
    getIntegrationAllowances: getAdminTargetIntegrationAllowances,
    getOrganization: getAdminTargetOrganization,
    getOrganizationSettings: getAdminTargetOrganizationSettings,
    updateOrganization: updateAdminTargetOrganization,
    updateOrganizationSetting: updateAdminTargetOrganizationSetting,
  },
} as const;

// These scope-first functions build or dispatch requests with contracts that
// do not match ordinary envelope endpoints. Keeping them explicit prevents a
// Response/request-builder API from being accidentally bound as an endpoint.
export const specialTransports = {
  imports: { buildImportPreviewCleanupRequest },
  postingTemplate: { makePostingChannelsReadRequest, makePostingChannelsWriteRequest },
} as const;

// Compile-time convention check: resolves to `true` only while no
// globalEndpoints member accepts a RequestScope first parameter; a violating
// member surfaces here as its [domain, operation] pair failing the `true`
// constraint. This is what keeps the two calling conventions from mixing
// again.
type ScopeFirstGlobalMembers<M> = {
  [D in keyof M]: { [K in keyof M[D]]: M[D][K] extends (scope: RequestScope, ...rest: never[]) => unknown ? [D, K] : never }[keyof M[D]];
}[keyof M];
type Expect<T extends true> = T;
export type GlobalEndpointsOwnTheirScope = Expect<
  [ScopeFirstGlobalMembers<typeof globalEndpoints>] extends [never] ? true : ScopeFirstGlobalMembers<typeof globalEndpoints>
>;

// Public operations are fixed to tenant scope `none` and opt out of session
// authentication explicitly inside their endpoint implementations. They are
// intentionally not bound to OrganizationScope even when their UI route has
// a `[sitename]` segment.
export const anonymousEndpoints = {
  invitations: { acceptInvite, getInviteByCode },
} as const;

type AnyScopedEndpoint = (scope: RequestScope, ...rest: never[]) => unknown;

type NonScopeFirstMembers<M> = {
  [D in keyof M]: {
    [K in keyof M[D]]: M[D][K] extends (scope: RequestScope, ...rest: never[]) => unknown ? never : [D, K];
  }[keyof M[D]];
}[keyof M];

export type ScopedEndpointsRequireScopeFirst = Expect<
  [NonScopeFirstMembers<typeof scopedEndpoints>] extends [never] ? true : NonScopeFirstMembers<typeof scopedEndpoints>
>;

export type BoundEndpoint<F> =
  F extends <T>(scope: RequestScope, ...rest: infer Rest) => Promise<import('@/types/endpoints').EndpointResponse<T>>
    ? <T>(...rest: Rest) => Promise<import('@/types/endpoints').EndpointResponse<T>>
    : F extends (scope: RequestScope, ...rest: infer Rest) => infer Result
      ? (...rest: Rest) => Result
      : never;
export type BoundDomain<D> = { [K in keyof D]: BoundEndpoint<D[K]> };
export type BoundEndpoints<M> = { [K in keyof M]: BoundDomain<M[K]> };

export type SessionEndpoints = BoundEndpoints<typeof scopedEndpoints> & {
  organization: OrganizationId;
};

export const bindScopedEndpoints = (organization: OrganizationId): SessionEndpoints => {
  const scope = requestScopeFor(organization);
  const bound: Record<string, Record<string, unknown>> = {};
  for (const [domain, operations] of Object.entries(scopedEndpoints as Record<string, Record<string, AnyScopedEndpoint>>)) {
    bound[domain] = {};
    for (const [name, operation] of Object.entries(operations)) {
      bound[domain][name] = (...rest: unknown[]) => operation(scope, ...(rest as never[]));
    }
  }
  return {
    ...(bound as BoundEndpoints<typeof scopedEndpoints>),
    organization,
  };
};
